import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useUser, useAuth } from '@clerk/clerk-react';
import axiosInstance from '../config/axios';
import { formatPickupWindow } from '../utils/dateTimeUtils';
import { useTranslation } from '../hooks/useTranslation';

interface OrderItem {
  id: string;
  product_id: string;
  product_name_en: string;
  product_name_zh: string;
  quantity_kg: number;
  price_per_kg: number;
  subtotal: number;
}

interface PickupDate {
  id: string;
  pickup_date: string;
  pickup_start_time: string;
  pickup_end_time: string;
  last_order_datetime: string | null;
  location_name: string;
  location_address: string;
}

interface Order {
  id: string;
  pickup_date_id: string;
  customer_name: string;
  customer_email: string;
  customer_phone: string;
  customer_address: string;
  total_amount: number;
  status: string;
  is_paid: boolean;
  created_at: string;
  paid_at: string | null;
  items: OrderItem[];
  pickup_date: PickupDate;
}

const ProfilePage: React.FC = () => {
  const { user, isLoaded: userLoaded } = useUser();
  const { isSignedIn, getToken } = useAuth();
  const { t, language } = useTranslation();

  // Fetch user's orders - only when user is authenticated
  const { data: orders, isLoading, error } = useQuery<Order[]>({
    queryKey: ['userOrders'],
    queryFn: async () => {
      // Ensure we have a fresh token before making the request
      const token = await getToken();
      if (!token) {
        throw new Error('No authentication token available');
      }
      
      const response = await axiosInstance.get('/api/orders');
      return response.data;
    },
    enabled: userLoaded && isSignedIn === true, // Only run query when user is authenticated
  });

  const getStatusColor = (status: string): string => {
    switch (status.toLowerCase()) {
      case 'paid':
        return '#28a745';
      case 'pending':
        return '#ffc107';
      case 'completed':
        return '#007bff';
      case 'cancelled':
        return '#dc3545';
      default:
        return '#6c757d';
    }
  };

  const getStatusBadge = (status: string, isPaid: boolean) => {
    const displayStatus = isPaid ? t('profile.statusPaid') : status;
    const statusKey = status.toLowerCase();
    let translatedStatus = displayStatus;
    
    if (!isPaid) {
      if (statusKey === 'pending') translatedStatus = t('profile.statusPending');
      else if (statusKey === 'completed') translatedStatus = t('profile.statusCompleted');
      else if (statusKey === 'cancelled') translatedStatus = t('profile.statusCancelled');
    }
    
    return (
      <span style={{
        padding: '4px 12px',
        borderRadius: '12px',
        fontSize: '14px',
        fontWeight: 'bold',
        backgroundColor: getStatusColor(isPaid ? 'Paid' : status),
        color: 'white',
      }}>
        {translatedStatus}
      </span>
    );
  };

  if (isLoading) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <p>{t('profile.loading')}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <p style={{ color: '#dc3545' }}>{t('profile.loadError')}</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
      <h1>{t('profile.title')}</h1>

      {/* Account Information */}
      <section style={{ marginBottom: '40px', padding: '20px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
        <h2>{t('profile.accountInformation')}</h2>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
          <div>
            <p style={{ margin: '5px 0' }}>
              <strong>{t('profile.name')}:</strong> {user?.fullName || t('profile.notProvided')}
            </p>
            <p style={{ margin: '5px 0' }}>
              <strong>{t('profile.email')}:</strong> {user?.primaryEmailAddress?.emailAddress || t('profile.notProvided')}
            </p>
          </div>
          <div>
            <p style={{ margin: '5px 0' }}>
              <strong>{t('profile.phone')}:</strong> {user?.primaryPhoneNumber?.phoneNumber || t('profile.notProvided')}
            </p>
            <p style={{ margin: '5px 0' }}>
              <strong>{t('profile.memberSince')}:</strong> {user?.createdAt ? new Date(user.createdAt).toLocaleDateString() : t('profile.unknown')}
            </p>
          </div>
        </div>
      </section>

      {/* Order History */}
      <section>
        <h2>{t('profile.orderHistory')}</h2>
        
        {orders && orders.length > 0 ? (
          <div style={{ display: 'grid', gap: '20px' }}>
            {orders.map(order => (
              <div
                key={order.id}
                style={{
                  padding: '20px',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  backgroundColor: 'white',
                }}
              >
                {/* Order Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px', paddingBottom: '15px', borderBottom: '2px solid #f0f0f0' }}>
                  <div>
                    <h3 style={{ margin: '0 0 5px 0' }}>{t('profile.orderNumber')}{order.id.slice(0, 8)}</h3>
                    <p style={{ margin: 0, color: '#666', fontSize: '14px' }}>
                      {t('profile.placedOn')} {new Date(order.created_at).toLocaleString()}
                    </p>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    {getStatusBadge(order.status, order.is_paid)}
                    <p style={{ margin: '5px 0 0 0', fontSize: '18px', fontWeight: 'bold' }}>
                      €{order.total_amount.toFixed(2)}
                    </p>
                  </div>
                </div>

                {/* Pickup Information */}
                <div style={{ marginBottom: '15px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                  <h4 style={{ margin: '0 0 10px 0' }}>{t('profile.pickupDetails')}</h4>
                  <p style={{ margin: '5px 0' }}>
                    <strong>{t('profile.dateTime')}:</strong> {formatPickupWindow(order.pickup_date.pickup_date, order.pickup_date.pickup_start_time, order.pickup_date.pickup_end_time, t('common.from'), t('common.to'))}
                  </p>
                  <p style={{ margin: '5px 0' }}>
                    <strong>{t('profile.location')}:</strong> {order.pickup_date.location_name}
                  </p>
                  <p style={{ margin: '5px 0', color: '#666', fontSize: '14px' }}>
                    {order.pickup_date.location_address}
                  </p>
                </div>

                {/* Order Items */}
                <div>
                  <h4 style={{ margin: '0 0 10px 0' }}>{t('profile.items')}</h4>
                  {order.items.map(item => (
                    <div
                      key={item.id}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        padding: '10px 0',
                        borderBottom: '1px solid #f0f0f0',
                      }}
                    >
                      <div>
                        <div style={{ fontWeight: 'bold' }}>{language === 'zh' ? item.product_name_zh : item.product_name_en}</div>
                        <div style={{ fontSize: '14px', color: '#666' }}>
                          {item.quantity_kg} kg × €{item.price_per_kg.toFixed(2)}/kg
                        </div>
                      </div>
                      <div style={{ fontWeight: 'bold' }}>
                        €{item.subtotal.toFixed(2)}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Payment Information */}
                {order.is_paid && order.paid_at && (
                  <div style={{ marginTop: '15px', padding: '10px', backgroundColor: '#d4edda', borderRadius: '4px', border: '1px solid #c3e6cb' }}>
                    <p style={{ margin: 0, fontSize: '14px', color: '#155724' }}>
                      <strong>{t('profile.paidOn')}</strong> {new Date(order.paid_at).toLocaleString()}
                    </p>
                  </div>
                )}

                {/* Pickup Reminder */}
                {order.is_paid && new Date(order.pickup_date.pickup_date) >= new Date() && (
                  <div style={{ marginTop: '15px', padding: '10px', backgroundColor: '#fff3cd', borderRadius: '4px', border: '1px solid #ffc107' }}>
                    <p style={{ margin: 0, fontSize: '14px' }}>
                      <strong>{t('profile.reminderTitle')}</strong> {t('profile.reminderText')}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div style={{ padding: '40px', textAlign: 'center', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
            <p style={{ fontSize: '18px', color: '#666', marginBottom: '20px' }}>
              {t('profile.noOrders')}
            </p>
            <a
              href="/order"
              style={{
                display: 'inline-block',
                padding: '12px 30px',
                backgroundColor: '#007bff',
                color: 'white',
                textDecoration: 'none',
                borderRadius: '4px',
                fontSize: '16px',
                fontWeight: 'bold',
              }}
            >
              {t('profile.placeFirstOrder')}
            </a>
          </div>
        )}
      </section>
    </div>
  );
};

export default ProfilePage;
